/**
 * Named searches, per list screen, in `localStorage`.
 *
 * Every list in this tree keeps its filters in the query string, which means a
 * search *is* a URL and a saved search is a URL with a name on it. That is the
 * whole feature — but it removes a real daily cost: the people who use this
 * admin retype the same four or five filters every morning ("signups today that
 * failed", "seasonal purchases still pending"), because there has never been
 * anywhere to put them.
 *
 * Deliberately local, not server-side. A saved search is somebody's own habit,
 * it holds no data, and an endpoint for it would need a schema, a migration and
 * a sharing model to earn the same result. If they turn out to be worth sharing,
 * the storage moves and nothing else does — and until then, "Copy link" already
 * covers sharing one.
 *
 * Every read is defensive: this is a string a person can edit, and a list page
 * that throws because of it would be a worse admin than one with no shortcuts.
 */

const KEY = 'ws-next-views';
const LIMIT_PER_SCREEN = 12;

const readAll = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeAll = value => {
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch (error) {
    // Full or disabled storage costs the shortcut, not the page.
  }
};

/** @returns {Array<{name: string, search: string}>} */
export const readViews = screenPath => {
  const list = readAll()[screenPath];
  return Array.isArray(list)
    ? list.filter(view => view && typeof view.name === 'string' && typeof view.search === 'string')
    : [];
};

/** Saving under an existing name replaces it — that is what "update" looks like. */
export const saveView = (screenPath, name, search) => {
  const all = readAll();
  const rest = readViews(screenPath).filter(view => view.name !== name);

  all[screenPath] = [{ name, search }, ...rest].slice(0, LIMIT_PER_SCREEN);
  writeAll(all);
  return all[screenPath];
};

export const removeView = (screenPath, name) => {
  const all = readAll();
  all[screenPath] = readViews(screenPath).filter(view => view.name !== name);
  writeAll(all);
  return all[screenPath];
};

/**
 * Every saved search across every screen, for the command palette. The screen
 * path travels with it so the palette can build the URL and say where it goes.
 */
export const readAllViews = () => Object.entries(readAll())
  .flatMap(([screenPath, list]) => (Array.isArray(list) ? list : [])
    .filter(view => view && typeof view.name === 'string' && typeof view.search === 'string')
    .map(view => ({ ...view, screenPath })));
